import * as Sentry from '@sentry/react';
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { PersistGate } from 'redux-persist/lib/integration/react';

import { SocketProvider } from '@/contexts/socket/SocketContext';
import { AnalyticsSession } from '@/platform/analytics/AnalyticsSession';
import { ROUTES } from '@/platform/router/routes';
import { analytics } from '@/shared/services/analytics.service';
import { ConfigService } from '@/shared/services/config.service';
import { configureHttpClient } from '@/shared/services/http.service';
import {
  applyRuntimeEndpoints,
  refreshRuntimeEndpoints,
} from '@/shared/services/runtime-endpoints';
import { migrateLegacyStorageKeys } from '@/shared/services/token.storage';
import { ThemeProvider } from '@/theme/ThemeProvider';

import { AppProvider } from './AppProvider';
import { AppRoot } from './AppRoot';
import type { AppDefinition } from './contracts';
import { createApp, type AppRuntime } from './createApp';

/**
 * Boots one dashboard: error reporting, product analytics, session plumbing,
 * runtime creation and the provider tree. Each `apps/<id>/src/main.tsx` imports its theme and calls
 * this with its definition.
 */
/** Marks the last automatic reload, so a broken chunk cannot loop the tab. */
const STALE_CHUNK_KEY = 'platform.chunk_reload_at';
const STALE_CHUNK_COOLDOWN_MS = 60_000;

/**
 * Reloads once when a lazy route asks for a chunk that a deploy has replaced.
 *
 * Every build hashes its chunk names, so a tab opened before a deploy still
 * holds the old ones. Cloudflare Pages does not 404 them — it answers with the
 * SPA fallback, which is `200 text/html`, and the browser refuses to run a
 * document as a module. The route then dies on "Failed to fetch dynamically
 * imported module", which reads to the user as the app being broken when the
 * only thing wrong is that their tab is one version behind.
 *
 * Vite dispatches `vite:preloadError` for exactly this; calling
 * `preventDefault` stops the throw and one reload picks up the new index and
 * the new names. Guarded by a timestamp because a chunk that is genuinely
 * missing would otherwise reload forever: the second failure inside a minute
 * is left to the error boundary, which is the honest outcome.
 */
function recoverFromStaleChunks(): void {
  window.addEventListener('vite:preloadError', (event) => {
    let lastReloadAt = 0;
    try {
      lastReloadAt = Number(window.sessionStorage.getItem(STALE_CHUNK_KEY)) || 0;
    } catch {
      // Blocked storage only costs the guard, not the recovery.
    }
    if (Date.now() - lastReloadAt < STALE_CHUNK_COOLDOWN_MS) return;

    event.preventDefault();
    try {
      window.sessionStorage.setItem(STALE_CHUNK_KEY, String(Date.now()));
    } catch {
      // Without the guard the cooldown is gone, so reload at most this once.
    }
    window.location.reload();
  });
}

export function bootstrap(app: AppDefinition, rootElement: HTMLElement): AppRuntime {
  const config = ConfigService.getInstance();

  // On an organization's custom domain: this origin's login page and the API
  // resolved on a previous visit, before anything issues a request.
  applyRuntimeEndpoints();

  // A Vite dev server only ever runs on a developer's machine, and both
  // `pnpm dev` and `pnpm local` carry a real DSN in their env file — so without
  // this guard every hot-reload crash and every half-written branch reports into
  // the same project as the deployed dashboards. Builds (development, qa,
  // production) are unaffected.
  const sentryDsn = config.get('isDev') ? undefined : config.get('sentryDsn');
  if (sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment: import.meta.env.MODE,
      integrations: [
        // Browser tracing is NOT a default integration in v10: without this,
        // `tracesSampleRate` does nothing and the project records zero spans.
        // The router variant is what names a transaction after its route
        // instead of the literal URL, which would be one name per org id.
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        // Sentry masks independently of PostHog, so the selectors the design
        // system already marks as sensitive are repeated here — otherwise a
        // replay would show exactly what analytics deliberately hides.
        Sentry.replayIntegration({
          mask: ['[data-ph-mask]'],
          block: ['.ph-no-capture'],
        }),
      ],
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,
      // Record a replay only when something actually broke: the free plan
      // includes 50 per month.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1,
      // Injected by the Vite factory from CF_PAGES_COMMIT_SHA or `git
      // rev-parse`. Without it a regression cannot be tied to a deploy.
      release: config.get('appCommit') || undefined,
      sendDefaultPii: true,
    });
    // Every dashboard reports into the same Sentry project, so this tag is what
    // tells their issues apart — the counterpart of PostHog's `app` property.
    Sentry.setTag('app', app.id);
  }

  // Product analytics: a no-op without VITE_POSTHOG_KEY (same convention as Sentry).
  const env = config.get('env');
  analytics.init({
    key: config.get('posthogKey') ?? '',
    host: config.get('posthogHost'),
    env,
    appId: app.id,
    appVersion: app.brand.version,
    commit: config.get('appCommit'),
    isProduction: env === 'production',
    isLocal: config.get('isDev'),
    tracingHosts: [
      ...new Set([
        new URL(config.getBuildValue('apiUrl')).host,
        new URL(config.getOrThrow('apiUrl')).host,
      ]),
    ],
  });

  if (import.meta.env.DEV && document.documentElement.dataset.brand !== app.id) {
    console.warn(
      `[platform] <html data-brand="${app.id}"> is missing in index.html; brand tokens fall back to the neutral ramp.`,
    );
  }

  recoverFromStaleChunks();

  migrateLegacyStorageKeys();

  // Wired before the router exists: creating a data router initializes it, so
  // any future route loader must already see the token and the 401 handler.
  // `resetState` is filled in as soon as the store exists.
  const session: { resetState: () => void } = { resetState: () => {} };
  configureHttpClient({
    getToken: app.auth.tokens.get,
    onUnauthorized: () => {
      // The page unloads right after: capture and reset here, not in an effect.
      analytics.capture('auth:session_ended', { reason: 'unauthorized' });
      analytics.reset();
      app.auth.tokens.clear();
      session.resetState();
      window.location.href = ROUTES.login;
    },
  });

  const runtime = createApp(app);
  session.resetState = runtime.resetState;

  // Never blocks the first paint: until it answers, requests go to the
  // platform's API, which accepts a verified custom origin too.
  void refreshRuntimeEndpoints();

  polyfillCountryFlagEmojis();

  // React 19 reports render errors through these hooks. Without them Sentry
  // still catches the throw via `window.onerror`, but loses the component
  // stack, which is the half that makes a React error actionable.
  createRoot(rootElement, {
    onUncaughtError: Sentry.reactErrorHandler(),
    onCaughtError: Sentry.reactErrorHandler(),
  }).render(
    <StrictMode>
      <AppProvider app={app}>
        <Provider store={runtime.store}>
          <PersistGate loading={null} persistor={runtime.persistor}>
            <ThemeProvider>
              <AnalyticsSession />
              <SocketProvider>
                <AppRoot router={runtime.router} />
              </SocketProvider>
            </ThemeProvider>
          </PersistGate>
        </Provider>
      </AppProvider>
    </StrictMode>,
  );

  return runtime;
}
